import React, { useState } from 'react';
import Footer from './Footer';
import { useNavigate, Link } from 'react-router-dom';

// Web3Forms access key tied to support@bargad.ai (submissions are emailed there).
// Create/replace this key at https://web3forms.com using support@bargad.ai as the recipient.
const WEB3FORMS_ACCESS_KEY =
  process.env.REACT_APP_WEB3FORMS_ACCESS_KEY || 'befdb1d9-3b42-4a13-a5ba-304a6df26b15';

const AskForAccess: React.FC = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    mobile: '',
    companyEmail: '',
    companyName: '',
    industry: '',
    location: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showMessagePopup, setShowMessagePopup] = useState(false);


  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    
    // Word limit validation for message field
    if (name === 'message') {
      const words = value.trim().split(/\s+/).filter(word => word.length > 0);
      if (words.length > 250) {
        return; // Don't update if exceeds 250 words
      }
    }
    
    setForm((prev) => ({ ...prev, [name]: value }));
  };
  
  const getWordCount = (text: string): number => {
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Validate message field
    if (!form.message.trim()) {
      setShowMessagePopup(true);
      return;
    }

    setSubmitError('');
    setSubmitting(true);

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          access_key: WEB3FORMS_ACCESS_KEY,
          subject: `New Access Request from ${form.firstName} ${form.lastName}`,
          from_name: `${form.firstName} ${form.lastName}`,
          'First Name': form.firstName,
          'Last Name': form.lastName,
          'Mobile Number': form.mobile,
          'Company Email': form.companyEmail,
          'Company Name': form.companyName,
          Industry: form.industry,
          Location: form.location,
          'Use Case': form.message,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setSubmitted(true);
      } else {
        setSubmitError(
          data.message || 'Something went wrong. Please try again or email support@bargad.ai.'
        );
      }
    } catch (err) {
      setSubmitError(
        'Unable to submit right now. Please check your connection or email support@bargad.ai.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-4">
        <div className="max-w-lg w-full text-center">
          <div className="mb-6">
            <svg
              className="w-20 h-20 text-[#26ab4d] mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2 className="text-3xl font-black text-white mb-3">Thanks!</h2>
          <p className="text-gray-300 mb-8">Your access request has been submitted successfully. Our team will get back to you within 24 hours.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/products"
              state={{ fromFormSubmission: true }}
              className="inline-block px-6 py-3 rounded-full bg-[#26ab4d] text-white font-semibold hover:bg-[#1e8a3f] transition-colors"
            >
              Explore Products
            </Link>
            <a
              href="/"
              className="inline-block px-6 py-3 rounded-full bg-transparent border-2 border-[#26ab4d] text-[#26ab4d] font-semibold hover:bg-[#26ab4d] hover:text-white transition-colors"
            >
              Go Home
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden flex flex-col">

      {/* Close/back arrow */}
      <button
        aria-label="Close"
        onClick={() => navigate('/')}
        className="absolute top-4 left-4 z-20 p-2 rounded-full border border-white/20 text-white hover:border-[#26ab4d] hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-[#26ab4d] transition-colors"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
      </button>

      {/* Centered Form */}
      <div className="container mx-auto px-[1.5rem] flex-1 pb-0 relative z-10 flex items-center justify-center py-8 lg:py-12 xl:py-[2rem]">
        <div className="w-full">
          {/* Heading Section - Aligned with arrow */}
          <div className="text-center mb-3 lg:mb-6 pt-2 lg:pt-4">
            <h1 className="text-[1.5rem] lg:text-[1.75rem] xl:text-[3rem] 2xl:text-[4rem] font-black mb-2 xl:mb-4 tracking-tight px-2">
              <span className="text-white">ASK FOR </span>
              <span className="text-[#26ab4d]">ACCESS</span>
            </h1>
            <p className="text-[0.8125rem] lg:text-[0.875rem] xl:text-[1rem] text-slate-300 max-w-2xl mx-auto leading-relaxed px-3 lg:px-4">
              Get started with our comprehensive risk intelligence platform. Fill out the form below and our team will get back to you.
            </p>
          </div>

          {/* Form - 2x4 Grid Layout */}
          <form onSubmit={handleSubmit} className="w-full">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 max-w-4xl mx-auto">
              {/* Row 1 - Personal Information Box */}
              <div className="flex flex-col">
                <label className="block text-[0.8125rem] lg:text-[0.875rem] xl:text-[1rem] font-semibold text-gray-200 mb-1.5 lg:mb-2 whitespace-nowrap">
                  First Name <span className="text-[#26ab4d]">*</span>
                </label>
                <input 
                  name="firstName" 
                  value={form.firstName} 
                  onChange={handleChange} 
                  className="w-full px-3 lg:px-4 py-2 lg:py-2.5 xl:py-3 rounded-full bg-[#26ab4d]/10 border border-[#26ab4d]/50 text-white text-[0.8125rem] lg:text-[0.875rem] xl:text-[1rem] placeholder-gray-400 outline-none focus:ring-2 focus:ring-[#26ab4d] focus:border-[#26ab4d] transition-all duration-200" 
                  required
                />
              </div>

              <div className="flex flex-col">
                <label className="block text-[0.8125rem] lg:text-[0.875rem] xl:text-[1rem] font-semibold text-gray-200 mb-1.5 lg:mb-2 whitespace-nowrap">
                  Last Name <span className="text-[#26ab4d]">*</span>
                </label>
                <input 
                  name="lastName" 
                  value={form.lastName} 
                  onChange={handleChange} 
                  className="w-full px-3 lg:px-4 py-2 lg:py-2.5 xl:py-3 rounded-full bg-[#26ab4d]/10 border border-[#26ab4d]/50 text-white text-[0.8125rem] lg:text-[0.875rem] xl:text-[1rem] placeholder-gray-400 outline-none focus:ring-2 focus:ring-[#26ab4d] focus:border-[#26ab4d] transition-all duration-200" 
                  required
                />
              </div>

              {/* Row 2 - Contact Information Box */}
              <div className="flex flex-col">
                <label className="block text-[0.8125rem] lg:text-[0.875rem] xl:text-[1rem] font-semibold text-gray-200 mb-1.5 lg:mb-2 whitespace-nowrap">
                  Mobile Number <span className="text-[#26ab4d]">*</span>
                </label>
                <input 
                  name="mobile" 
                  value={form.mobile} 
                  onChange={handleChange} 
                  className="w-full px-3 lg:px-4 py-2 lg:py-2.5 xl:py-3 rounded-full bg-[#26ab4d]/10 border border-[#26ab4d]/50 text-white text-[0.8125rem] lg:text-[0.875rem] xl:text-[1rem] placeholder-gray-400 outline-none focus:ring-2 focus:ring-[#26ab4d] focus:border-[#26ab4d] transition-all duration-200" 
                  required
                />
              </div>

              <div className="flex flex-col">
                <label className="block text-[0.8125rem] lg:text-[0.875rem] xl:text-[1rem] font-semibold text-gray-200 mb-1.5 lg:mb-2 whitespace-nowrap">
                  Company Email <span className="text-[#26ab4d]">*</span>
                </label>
                <input 
                  type="email" 
                  name="companyEmail" 
                  value={form.companyEmail} 
                  onChange={handleChange} 
                  className="w-full px-3 lg:px-4 py-2 lg:py-2.5 xl:py-3 rounded-full bg-[#26ab4d]/10 border border-[#26ab4d]/50 text-white text-[0.8125rem] lg:text-[0.875rem] xl:text-[1rem] placeholder-gray-400 outline-none focus:ring-2 focus:ring-[#26ab4d] focus:border-[#26ab4d] transition-all duration-200" 
                  required
                />
              </div>

              {/* Row 3 - Company Information Box */}
              <div className="flex flex-col">
                <label className="block text-[0.8125rem] lg:text-[0.875rem] xl:text-[1rem] font-semibold text-gray-200 mb-1.5 lg:mb-2 whitespace-nowrap">
                  Company Name <span className="text-[#26ab4d]">*</span>
                </label>
                <input 
                  name="companyName" 
                  value={form.companyName} 
                  onChange={handleChange} 
                  className="w-full px-3 lg:px-4 py-2 lg:py-2.5 xl:py-3 rounded-full bg-[#26ab4d]/10 border border-[#26ab4d]/50 text-white text-[0.8125rem] lg:text-[0.875rem] xl:text-[1rem] placeholder-gray-400 outline-none focus:ring-2 focus:ring-[#26ab4d] focus:border-[#26ab4d] transition-all duration-200" 
                  required
                />
              </div>

              <div className="flex flex-col">
                <label className="block text-[0.8125rem] lg:text-[0.875rem] xl:text-[1rem] font-semibold text-gray-200 mb-1.5 lg:mb-2 whitespace-nowrap">
                  Industry <span className="text-[#26ab4d]">*</span>
                </label>
                <input 
                  name="industry" 
                  value={form.industry} 
                  onChange={handleChange} 
                  className="w-full px-3 lg:px-4 py-2 lg:py-2.5 xl:py-3 rounded-full bg-[#26ab4d]/10 border border-[#26ab4d]/50 text-white text-[0.8125rem] lg:text-[0.875rem] xl:text-[1rem] placeholder-gray-400 outline-none focus:ring-2 focus:ring-[#26ab4d] focus:border-[#26ab4d] transition-all duration-200" 
                  required
                />
              </div>

              {/* Row 4 - Additional Information Box */}
              <div className="flex flex-col">
                <label className="block text-[0.8125rem] lg:text-[0.875rem] xl:text-[1rem] font-semibold text-gray-200 mb-1.5 lg:mb-2 whitespace-nowrap">
                  Location <span className="text-[#26ab4d]">*</span>
                </label>
                <input 
                  name="location" 
                  value={form.location} 
                  onChange={handleChange} 
                  className="w-full px-3 lg:px-4 py-2 lg:py-2.5 xl:py-3 rounded-full bg-[#26ab4d]/10 border border-[#26ab4d]/50 text-white text-[0.8125rem] lg:text-[0.875rem] xl:text-[1rem] placeholder-gray-400 outline-none focus:ring-2 focus:ring-[#26ab4d] focus:border-[#26ab4d] transition-all duration-200" 
                  required
                />
              </div>

              <div className="flex flex-col">
                <label className="block text-[0.8125rem] lg:text-[0.875rem] xl:text-[1rem] font-semibold text-gray-200 mb-1.5 lg:mb-2 whitespace-nowrap">
                  Explain your use case <span className="text-[#26ab4d]">*</span>
                </label>
                <button
                  type="button"
                  onClick={() => setShowMessagePopup(true)}
                  className={`w-full px-3 lg:px-4 py-2 lg:py-2.5 xl:py-3 rounded-full bg-[#26ab4d]/10 border border-[#26ab4d]/50 text-left text-[0.8125rem] lg:text-[0.875rem] xl:text-[1rem] placeholder-gray-400 outline-none focus:ring-2 focus:ring-[#26ab4d] focus:border-[#26ab4d] transition-all duration-200 min-h-[2.5rem] lg:min-h-[2.75rem] xl:min-h-[3rem] ${form.message ? 'text-white font-medium' : 'text-gray-400'}`}
                >
                  {form.message || '\u00A0'}
                </button>
              </div>
            </div>

            {/* Submit Button - Centered */}
            <div className="flex flex-col items-center mt-8 lg:mt-12 mb-6 lg:mb-8">
              {submitError && (
                <p className="mb-3 text-[0.8125rem] lg:text-[0.875rem] text-red-400 text-center max-w-md px-4">
                  {submitError}
                </p>
              )}
              <button 
                type="submit" 
                disabled={submitting}
                className="px-5 lg:px-8 py-2 lg:py-3 xl:py-4 rounded-full bg-[#26ab4d] hover:bg-[#1e8a3f] text-white text-[0.8125rem] lg:text-[0.875rem] xl:text-[1rem] font-bold tracking-wide transition-all duration-300 whitespace-nowrap min-w-[120px] lg:min-w-[170px] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? 'Submitting…' : 'Submit'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Message Popup Modal */}
      {showMessagePopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 lg:p-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-6 xl:p-8 max-w-2xl w-full max-h-[35rem] lg:max-h-[40rem] overflow-y-auto">
            <div className="flex justify-between items-center mb-3 lg:mb-4">
              <h3 className="text-[1.25rem] lg:text-[1.5rem] xl:text-[2rem] font-bold text-gray-900">Explain your use case</h3>
              <button
                type="button"
                onClick={() => setShowMessagePopup(false)}
                className="text-gray-500 hover:text-gray-700 transition-colors"
                aria-label="Close"
              >
                <svg className="w-5 h-5 lg:w-6 lg:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <textarea
              name="message"
              value={form.message}
              onChange={handleChange}
              placeholder="Explain in brief (200 - 250 words)"
              className="w-full px-3 lg:px-4 py-2.5 lg:py-3 rounded-lg bg-gray-50 border border-gray-300 text-gray-900 text-[0.8125rem] lg:text-[0.875rem] xl:text-[1rem] placeholder-gray-500 outline-none focus:ring-2 focus:ring-[#26ab4d] focus:border-[#26ab4d] transition-all duration-200 resize-none min-h-[10rem] lg:min-h-[12.5rem]"
            />
            <div className="flex justify-between items-center mt-2">
              <span className="text-xs lg:text-sm text-gray-500">
                {getWordCount(form.message)} / 250 words
              </span>
              {getWordCount(form.message) > 250 && (
                <span className="text-xs lg:text-sm text-red-500">
                  Maximum 250 words allowed
                </span>
              )}
            </div>
            <div className="flex justify-end mt-3 lg:mt-4 gap-2 lg:gap-3">
              <button
                type="button"
                onClick={() => setShowMessagePopup(false)}
                className="px-4 lg:px-6 py-2 rounded-full bg-gray-200 text-gray-700 text-[0.8125rem] lg:text-[0.875rem] xl:text-[1rem] font-semibold hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => setShowMessagePopup(false)}
                className="px-4 lg:px-6 py-2 rounded-full bg-[#26ab4d] text-white text-[0.8125rem] lg:text-[0.875rem] xl:text-[1rem] font-semibold hover:bg-[#1e8a3f] transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default AskForAccess;

